/**
 * The app-level multitask window layer's state (2026-08-02 Miles directive: a preview can become a fully
 * functional draggable/resizable window that stays with the user on ANY page; lifted out of
 * src/components/FilePreview/FloatingPreviewContext.tsx unchanged, PV5-8:C part 1, 2026-10-13). The provider
 * that draws the windows is mounted once at the App root, so windows survive route changes; every preview shell
 * offers a "Float" control that hands its payload here. Consumers use useFloatingPreview(); components rendered
 * outside the provider (unit tests, storybook) get a null context and simply hide their Float controls.
 *
 * Same-day amendment (drone-sim report): the layer also owns an app-level modal
 * previewer + a document-level scene-link interceptor, so clicking ANY raw
 * `….3d.html` link — in chat history, on any page — opens the in-platform preview
 * canvas instead of navigating to the bare VM page.
 *
 * The context lives here, in a module that draws nothing, so the shells that read it and the provider that
 * draws them never import each other.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { SerializedAttachment } from '../../components/GlobalChatSystem/types';
import { fileAttachmentFromUrl, shouldInterceptLinkClick } from '../../components/FilePreview/fileLinks';
import type { PreviewOriginContext } from './previewOrigin';
import { defaultFloatingRect, WindowRect, FLOATING_PREVIEW_BASE_Z } from '../../components/FilePreview/windowGeometry';
import {
  companionReservedRightPx,
  readChatDockedAttribute,
} from '../../components/GlobalChatSystem/dockedCompanion';

export type FloatingPayload =
  | {
      kind: 'attachment';
      file?: File | null;
      serialized?: SerializedAttachment | null;
      title: string;
      /**
       * Where the file came from (2026-08-24). Previously dropped at the
       * float handoff, which cost the window BOTH the server resolver's
       * locator-free fallback (messageId) and any way back to the file's
       * conversation. Optional — a link-opened preview has no origin.
       */
      context?: PreviewOriginContext | null;
    }
  | { kind: '3d'; artifactId: string; title: string };

export interface FloatingWindowState {
  id: string;
  payload: FloatingPayload;
  rect: WindowRect;
  z: number;
}

export interface FloatingPreviewApi {
  openFloating: (payload: FloatingPayload) => void;
  /** Open the app-level modal previewer (used by the scene-link interceptor and any
   *  surface without its own overlay mount). */
  openPreviewModal: (payload: { file?: File | null; serialized?: SerializedAttachment | null }) => void;
}

export const FloatingPreviewContext = createContext<FloatingPreviewApi | null>(null);

/** Null outside the provider — callers hide their Float controls in that case. */
export function useFloatingPreview(): FloatingPreviewApi | null {
  return useContext(FloatingPreviewContext);
}

export interface FloatingPreviewsView {
  api: FloatingPreviewApi;
  windows: FloatingWindowState[];
  /** The app-level modal previewer's file, while it is open. */
  modal: { file?: File | null; serialized?: SerializedAttachment | null } | null;
  closeModal: () => void;
  close: (id: string) => void;
  focus: (id: string) => void;
  setRect: (id: string, rect: WindowRect) => void;
  /** Where the windows are drawn: the document's body, above every page. */
  portalTarget: HTMLElement;
}

export function useFloatingPreviews(): FloatingPreviewsView {
  const [windows, setWindows] = useState<FloatingWindowState[]>([]);
  const [modal, setModal] = useState<{ file?: File | null; serialized?: SerializedAttachment | null } | null>(null);
  const counter = useRef(0);
  const zCounter = useRef(FLOATING_PREVIEW_BASE_Z);

  const openFloating = useCallback((payload: FloatingPayload) => {
    counter.current += 1;
    zCounter.current += 1;
    // Open in the space the chat is NOT using (2026-08-24): a float opened
    // while the pane is docked used to land exactly on top of it — both are
    // right-aligned by default. Read the rail straight off the root stamp;
    // an imperative callback has no business holding chat state.
    const rect = defaultFloatingRect(
      window.innerWidth,
      window.innerHeight,
      companionReservedRightPx({
        docked: readChatDockedAttribute(),
        viewportWidth: window.innerWidth,
      }),
    );
    // Stagger repeat windows so they don't stack invisibly.
    const offset = ((counter.current - 1) % 5) * 28;
    setWindows((ws) => [...ws, {
      id: `fpw-${counter.current}`,
      payload,
      rect: { ...rect, x: Math.max(8, rect.x - offset), y: rect.y + offset },
      z: zCounter.current,
    }]);
  }, []);

  const openPreviewModal = useCallback((payload: { file?: File | null; serialized?: SerializedAttachment | null }) => {
    setModal(payload);
  }, []);

  const close = useCallback((id: string) => {
    setWindows((ws) => ws.filter((w) => w.id !== id));
  }, []);

  const focus = useCallback((id: string) => {
    zCounter.current += 1;
    const z = zCounter.current;
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, z } : w)));
  }, []);

  const setRect = useCallback((id: string, rect: WindowRect) => {
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, rect } : w)));
  }, []);

  // File-link interceptor (capture phase — must beat target=_blank navigation):
  // any anchor whose href is a PLATFORM-HOSTED previewable file (scenes, xlsx,
  // pdf, media, …) opens the standard in-platform previewer instead of a new
  // tab (2026-08-03 directive). Ordinary web links are never intercepted, and
  // the previewer keeps open-in-tab + download as explicit options.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const anchor = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      // Decision is pure (fileLinks.shouldInterceptLinkClick) so every branch is
      // unit-pinned — notably: a PROGRAMMATIC click is never intercepted, because
      // the previewer's own download helper appends `<a download>` and clicks it,
      // and cancelling that killed every Download button (review 2026-08-03).
      if (!shouldInterceptLinkClick({
        isTrusted: e.isTrusted,
        button: e.button,
        hasModifier: e.metaKey || e.ctrlKey || e.shiftKey || e.altKey,
        defaultPrevented: e.defaultPrevented,
        hasDownloadAttribute: anchor.hasAttribute('download'),
        href,
      })) return;
      const fileAtt = fileAttachmentFromUrl(href);
      if (!fileAtt) return;
      e.preventDefault();
      e.stopPropagation();
      setModal({ serialized: fileAtt as SerializedAttachment });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  const api = useMemo(() => ({ openFloating, openPreviewModal }), [openFloating, openPreviewModal]);
  const closeModal = useCallback(() => setModal(null), []);

  return { api, windows, modal, closeModal, close, focus, setRect, portalTarget: document.body };
}
